// Chakra imports
import {
	Box,
	Button,
	Flex,
	Input,
	useColorModeValue,
	useToast,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useGetLineRestrictions } from 'services/queryClient/wrapperHooks/useGetLineRestrictions';
import {
	createBulkCreateErrorLog,
	getEventUrlByModule,
	initializeBulkCreateLog,
} from 'utils/logging/bulkCreateLogger';
import { read, utils } from 'xlsx';

function Dropzone(props) {
	const {
		openLoading,
		setUploadPercentage,
		uploadPercentage,
		mutateAsync,
		content,
		onClose,
		isOpen,
		stringInvalidQuery,
		module,
		onCloseDropzone,
		onOpenTableModal,
		setNameFile,
		overwriteExistingData,
		setOverwriteExistingData,
		setSucessUpload,
		handleOnUploadSuccess,
		...rest
	} = props;

	const toast = useToast();
	const queryClient = useQueryClient();
	const { data: lineRestrictions } = useGetLineRestrictions();

	const generateUploadTag = () => {
		return `${new Date().toISOString()}_${Math.random()
			.toString(36)
			.substring(2, 9)}`;
	};

	const onDrop = useCallback(
		async acceptedFiles => {
			openLoading();
			onClose();
			onOpenTableModal();

			const data = new FormData();

			data.append('file', acceptedFiles[0]);

			setNameFile(acceptedFiles[0].name);
			const reader = new FileReader();
			reader.readAsBinaryString(acceptedFiles[0]);
			reader.onload = async e => {
				/* create workbook */
				const binarystr = e.target.result;
				const wb = read(binarystr, {
					type: 'binary',
					cellDates: true,
					cellNF: false,
				});

				/* selected the first sheet */
				const wsname = wb.SheetNames[0];
				const ws = wb.Sheets[wsname];
				// Ler o cabeçalho
				const header = utils.sheet_to_json(ws, {
					range: 'A1:HZ1',
					header: 1,
				})[0];

				const primiraLinha = utils.sheet_to_json(ws, {
					range: 'A3:HZ3',
					header: 1,
				})[0];

				const qtdLinhasPorBloco =
					lineRestrictions?.quantidadeLinhasImportacaoArquivosPorVez ||
					50;
				const fileSize = ws['!ref']
					.replaceAll('A1', '')
					.match(/\d+/g)[0];
				if (fileSize <= 0 || primiraLinha.length <= 0) {
					toast({
						title: 'Erro ao enviar arquivo!',
						description:
							'O Arquivo enviado não está preenchido. Verifique.',
						position: 'bottom-left',
						status: 'error',
						duration: 8000,
						isClosable: true,
					});
					onCloseDropzone();
					return;
				}
				let success = true;
				setUploadPercentage(0);
				setSucessUpload(false);
				let tag = generateUploadTag();
				let totalBlocos = 0;
				let blocosProcessados = 0;

				const eventUrl = getEventUrlByModule(module);
				if (eventUrl) {
					initializeBulkCreateLog(eventUrl, acceptedFiles[0].name);
				}

				if (overwriteExistingData && module === 'produto') {
					const totalLinhas = parseInt(fileSize);
					localStorage.setItem(
						'product-upload-processing',
						Date.now().toString()
					);
					localStorage.setItem(
						'product-upload-blocks-processing',
						'true'
					);
					localStorage.setItem(
						'product-upload-file-lines',
						totalLinhas.toString()
					);
					setTimeout(() => {
						window.dispatchEvent(
							new Event('product-upload-processing-changed')
						);
					}, 0);
					toast({
						title: 'Arquivo carregado!',
						position: 'bottom-left',
						status: 'info',
						duration: 8000,
						isClosable: true,
					});
				}
				for (let iBloco = 0; true; iBloco++) {
					const qtdLinhas = iBloco * qtdLinhasPorBloco;
					let res;
					try {
						const startRow = qtdLinhas + 2;
						const endRow = startRow + qtdLinhasPorBloco - 1;
						const range = `A${startRow + 1}:HZ${endRow + 1}`;
						const blocoArray = utils.sheet_to_json(ws, {
							range: range,
							header: 1,
						});
						if (
							blocoArray.length <= 0 ||
							blocoArray[0].length <= 0
						) {
							if (iBloco === 0) {
								totalBlocos = 0;
							} else if (
								overwriteExistingData &&
								module === 'produto' &&
								blocosProcessados > 0
							) {
								localStorage.removeItem(
									'product-upload-blocks-processing'
								);
								if (tag) {
									localStorage.setItem(
										'product-upload-last-block-tag',
										tag
									);
								}
								window.dispatchEvent(
									new Event(
										'product-upload-processing-changed'
									)
								);
							}
							break;
						}
						if (iBloco === 0) {
							const totalLinhas = parseInt(fileSize);
							totalBlocos = Math.ceil(
								totalLinhas / qtdLinhasPorBloco
							);
						}

						const bloco = [];
						for (let iRow = 0; iRow < blocoArray.length; iRow++) {
							const row = blocoArray[iRow];
							if (row.length <= 0) {
								break;
							}
							const data = {};
							header.forEach((key, iHeader) => {
								data[key] = row[iHeader];
								data.numero_linha = qtdLinhas + iRow + 3;
							});
							bloco.push(data);
						}
						const body = {
							arquivo: bloco,
							atualizar: overwriteExistingData,
							arquivo_tag: tag,
						};

						res = await mutateAsync(body);

						const percentage = (100 * qtdLinhas) / fileSize;
						if (res?.quantidade_erros) {
							success = false;
							if (overwriteExistingData && module === 'produto') {
								localStorage.removeItem(
									'product-upload-processing'
								);
								localStorage.removeItem(
									'product-upload-blocks-processing'
								);
								localStorage.removeItem(
									'product-upload-file-lines'
								);
								window.dispatchEvent(
									new Event(
										'product-upload-processing-changed'
									)
								);
							}
							break;
						}
						tag = res.arquivo_tag;
						setUploadPercentage(percentage);
						blocosProcessados++;
						if (overwriteExistingData && module === 'produto') {
							if (blocosProcessados < totalBlocos) {
								localStorage.setItem(
									'product-upload-blocks-processing',
									'true'
								);
							} else {
								localStorage.removeItem(
									'product-upload-blocks-processing'
								);
								localStorage.setItem(
									'product-upload-last-block-tag',
									res.arquivo_tag || tag
								);
								window.dispatchEvent(
									new Event(
										'product-upload-processing-changed'
									)
								);
							}
						}
					} catch (error) {
						console.error(
							'Erro ao realizar o upload do arquivo!',
							error
						);
						success = false;
						if (overwriteExistingData && module === 'produto') {
							const eventUrl = getEventUrlByModule(module);
							if (eventUrl) {
								await createBulkCreateErrorLog(eventUrl, {
									message:
										'Erro ao realizar o upload do arquivo',
								});
							}

							localStorage.removeItem(
								'product-upload-processing'
							);
							localStorage.removeItem(
								'product-upload-blocks-processing'
							);
							localStorage.removeItem(
								'product-upload-file-lines'
							);
							window.dispatchEvent(
								new Event('product-upload-processing-changed')
							);
						}
					}
				}
				if (success) {
					if (!(overwriteExistingData && module === 'produto')) {
						toast({
							title: 'Sucesso!',
							description: overwriteExistingData
								? 'Arquivo enviado com sucesso. Itens criados e atualizados com sucesso.'
								: 'Arquivo enviado com sucesso. Itens criados com sucesso.',
							position: 'bottom-left',
							status: 'success',
							duration: 8000,
							isClosable: true,
						});
					}
					if (overwriteExistingData && module === 'produto') {
						localStorage.removeItem(
							'product-upload-blocks-processing'
						);
						window.dispatchEvent(
							new Event('product-upload-processing-changed')
						);
					}
				} else {
					if (overwriteExistingData && module === 'produto') {
						const eventUrl = getEventUrlByModule(module);
						if (eventUrl) {
							await createBulkCreateErrorLog(eventUrl, {
								message: 'Erro ao processar o arquivo',
							});
						}

						localStorage.removeItem('product-upload-processing');
						localStorage.removeItem(
							'product-upload-blocks-processing'
						);
						localStorage.removeItem('product-upload-file-lines');
						window.dispatchEvent(
							new Event('product-upload-processing-changed')
						);
					}
				}
				queryClient.invalidateQueries(stringInvalidQuery);
				setTimeout(() => onCloseDropzone(), 1000);
				setOverwriteExistingData(false);
			};
		},
		[overwriteExistingData]
	);

	const { isDragActive, getRootProps, getInputProps, isDragReject } =
		useDropzone({
			onDrop,
			accept: 'application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, text/csv',
		});

	const bg = useColorModeValue('secondaryGray.300', 'secondaryGray.400');
	const borderColor = useColorModeValue(
		'secondaryGray.400',
		'whiteAlpha.100'
	);

	return (
		<Box>
			<Flex
				align='center'
				justify='center'
				bg={bg}
				border='1px dashed'
				borderColor={borderColor}
				borderRadius='16px'
				w='100%'
				h='max-content'
				minH='100%'
				cursor='pointer'
				{...getRootProps({ className: 'dropzone' })}
				{...rest}
			>
				<Input
					variant='main'
					{...getInputProps()}
				/>
				<Button
					variant='no-effects'
					h={'320'}
				>
					{!isDragActive && content}
					{isDragActive && !isDragReject && 'Solte seu arquivo aqui!'}
					{isDragReject &&
						'Formato de arquivo inválido! Somente xlsx é permitido.'}
				</Button>
			</Flex>
		</Box>
	);
}

export default Dropzone;
